import React, { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import { pref } from '../utils/moneda';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';

// Sin tildes ni mayúsculas, para que "azucar" encuentre "Azúcar".
const norm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function fechaHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Inventario a una fecha: por producto, la cantidad del último cierre o ajuste
// que lo incluye hasta ese día, más compras y menos ventas facturadas desde
// entonces, valorizada al costo de esa fecha.
export default function InventarioActual() {
  const { empresaActiva, empresaActivaNombre } = useAuth();

  const [fecha, setFecha] = useState(fechaHoy());
  const [tasa, setTasa] = useVistaMoneda();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    if (!empresaActiva || !fecha) return;
    let vigente = true;
    setCargando(true);
    setError('');
    api.get('/inventario-cierre/actual', { params: { fecha, tasa } })
      .then(({ data }) => { if (vigente) setDatos(data); })
      .catch((err) => { if (vigente) setError(err.response?.data?.error || 'No se pudo calcular el inventario'); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [empresaActiva, fecha, tasa]);

  const texto = norm(busqueda.trim());
  const detalle = (datos?.detalle || []).filter((d) => !texto || norm(d.producto_nombre).includes(texto));
  const filasPorTipo = detalle.reduce((acc, d) => {
    if (!acc[d.tipo]) acc[d.tipo] = [];
    acc[d.tipo].push(d);
    return acc;
  }, {});
  const p = pref(tasa);

  // Lo que se ve (con la búsqueda aplicada), una fila por producto bajo su tipo.
  function contenidoExcel() {
    const filas = [];
    datos.porTipo.filter((t) => filasPorTipo[t.tipo]).forEach((t) => {
      const lista = filasPorTipo[t.tipo];
      filas.push({ valores: [t.tipo_label, null, '', '', null, null, null, null, lista.reduce((s, d) => s + (Number(d.valor) || 0), 0)], negrita: true });
      lista.forEach((d) => filas.push([
        d.producto_nombre, Number(d.cantidad), d.unidad_medida,
        `${d.documento.tipo === 'ajuste' ? 'Ajuste' : 'Cierre'} ${d.documento.numero}`,
        Number(d.conteo), Number(d.entradas), Number(d.salidas),
        d.costo_unitario !== null ? Number(d.costo_unitario) : 'sin costo',
        d.valor !== null ? Number(d.valor) : null
      ]));
    });
    return {
      titulo: 'Inventario actual',
      detalle: `Al ${formatFecha(fecha)} · costos en ${tasa}${texto ? ` · búsqueda "${busqueda.trim()}"` : ''}`,
      hojas: [{
        nombre: 'Inventario',
        columnas: [
          { titulo: 'Producto' }, { titulo: 'Cantidad', tipo: 'precio' }, { titulo: 'Unidad' }, { titulo: 'Último conteo' },
          { titulo: 'Contado', tipo: 'precio' }, { titulo: 'Entradas desde el conteo', tipo: 'precio' }, { titulo: 'Salidas desde el conteo', tipo: 'precio' },
          { titulo: `Costo unitario ${tasa}`, tipo: 'precio' }, { titulo: `Valor ${tasa}`, tipo: 'monto' }
        ],
        filas,
        total: ['Total', null, '', '', null, null, null, null, detalle.reduce((s, d) => s + (Number(d.valor) || 0), 0)]
      }]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Inventario actual" detalle={`Al ${formatFecha(fecha)} · costos en ${tasa}`} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Inventario actual
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <SelectorMoneda vista={tasa} onChange={setTasa} />
          <Exportar excel={contenidoExcel} disabled={cargando || !datos || detalle.length === 0} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-500">Inventario al</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="border rounded-md px-3 py-1.5 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar producto…"
              className="text-sm flex-1 outline-none min-w-0"
            />
            {busqueda && (
              <button type="button" onClick={() => setBusqueda('')} title="Limpiar búsqueda" className="text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 print:hidden">
          Cada producto parte del último cierre o ajuste que lo incluye hasta esa fecha, suma las compras y resta las ventas facturadas desde ese día (las notas de crédito devuelven, las anuladas no cuentan), y se valoriza al costo de ese día. La producción no entra: la corrige el próximo cierre.
        </p>

        {error && <p className="text-red-600 text-sm">{error}</p>}
        {cargando && <p className="text-sm text-slate-400">Calculando inventario...</p>}

        {!cargando && datos && datos.detalle.length === 0 && (
          <p className="text-sm text-slate-400">No hay cierres ni ajustes cargados hasta el {formatFecha(datos.fecha)}.</p>
        )}

        {!cargando && datos && datos.detalle.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-2">Producto</th>
                  <th className="p-2 text-right">Cantidad</th>
                  <th className="p-2">Último conteo</th>
                  <th className="p-2 text-right">Desde el conteo</th>
                  <th className="p-2 text-right">Costo unitario</th>
                  <th className="p-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {texto && detalle.length === 0 && (
                  <tr className="border-t"><td colSpan={6} className="p-2 text-slate-400">Ningún producto coincide con "{busqueda.trim()}".</td></tr>
                )}
                {datos.porTipo.filter((t) => !texto || filasPorTipo[t.tipo]).map((t) => (
                  <React.Fragment key={t.tipo}>
                    <tr className="border-t bg-slate-50/60">
                      <td colSpan={5} className="p-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{t.tipo_label}</td>
                      <td className="p-2 text-right font-medium tabular-nums">
                        {p}{formatMonto(texto ? filasPorTipo[t.tipo].reduce((sum, d) => sum + (Number(d.valor) || 0), 0) : t.valor)}
                      </td>
                    </tr>
                    {(filasPorTipo[t.tipo] || []).map((d) => (
                      <tr key={d.producto_id} className="border-t hover:bg-slate-50 transition-colors">
                        <td className="p-2">{d.producto_nombre}</td>
                        <td className={`p-2 text-right whitespace-nowrap tabular-nums ${d.cantidad < 0 ? 'text-red-600' : ''}`}>{formatMonto(d.cantidad, 4)} {d.unidad_medida}</td>
                        <td className="p-2 whitespace-nowrap text-slate-500">
                          {d.documento.tipo === 'ajuste' ? 'Ajuste' : 'Cierre'} {d.documento.numero}
                          {(d.entradas > 0 || d.salidas > 0) && <span className="text-slate-400"> · {formatMonto(d.conteo, 4)}</span>}
                        </td>
                        <td className="p-2 text-right whitespace-nowrap tabular-nums text-xs" title="Entradas: compras y notas de crédito de venta. Salidas: ventas y notas de crédito de compra.">
                          {d.entradas > 0 && <span className="text-green-700">+{formatMonto(d.entradas, 4)} entradas</span>}
                          {d.entradas > 0 && d.salidas > 0 && <br />}
                          {d.salidas > 0 && <span className="text-red-600">−{formatMonto(d.salidas, 4)} salidas</span>}
                          {!d.entradas && !d.salidas && <span className="text-slate-300">-</span>}
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          {d.costo_unitario !== null ? `${p}${formatMonto(d.costo_unitario, 6)}` : 'sin costo'}
                        </td>
                        <td className="p-2 text-right tabular-nums">{d.valor !== null ? `${p}${formatMonto(d.valor)}` : '-'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t font-medium">
                  <td colSpan={5} className="p-2">{texto ? 'Total de lo buscado' : 'Total'}</td>
                  <td className="p-2 text-right tabular-nums">
                    {p}{formatMonto(texto ? detalle.reduce((sum, d) => sum + (Number(d.valor) || 0), 0) : datos.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
